import { useEffect, useState } from 'react';
import { CircularProgress, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import { getThumbnailFromFile } from 'utils';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DefaultButton from '../DefaultButton';
import DefaultCheckbox from '../DefaultCheckbox';
import toast from 'react-hot-toast';
import uploadLogo from '_assets/parameters/upload.svg';
import useStyles from '../PostForm/styles';
import useLocationSearch from '../../hooks/useLocationSearch';
import { Autocomplete } from '@material-ui/lab';

const validationSchema = Yup.object({
  comment: Yup.string(),
  file: Yup.mixed().required(),
  hashtag: Yup.string(),
  isDraft: Yup.boolean(),
  location: Yup.mixed(),
});

const getInitialFileData = (post = null) => {
  const fileData = post?.file;
  return {
    data: fileData?.data ?? '',
    thumbnail: fileData?.thumbnail ?? '',
    type: fileData?.type ?? '',
    url: fileData?.url ?? post?.thumbnailUrl ?? '',
  };
};

function PostForm({
  disabled,
  disableSubmit,
  onCancel = () => {},
  onFieldChange = () => {},
  onFileChange = () => {},
  onSubmit = () => {},
  post,
}) {
  const classes = useStyles();

  const { errors, handleChange, handleSubmit, isSubmitting, setErrors, setFieldValue, setValues, touched, values } =
    useFormik({
      initialValues: { ...post, isDraft: post.state === 'draft' },
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        const result = await onSubmit(values, { ...fileData });
        if (result.success) {
          const data = getInitialFileData();
          setFileData(data);
          setAttempted(false);
        }
      },
    });

  const [attempted, setAttempted] = useState(false);
  const [fileData, setFileData] = useState(getInitialFileData(post));

  const fileError = attempted && Boolean(errors.file);

  const [inputLocation, setInputLocation] = useState('');
  const { locations } = useLocationSearch(inputLocation);

  useEffect(() => {
    setValues({ ...post, file: post?.file ?? post?.thumbnailUrl ?? null, isDraft: post.state === 'draft' });
    setFileData(getInitialFileData(post));
    setErrors({});
  }, [post, setErrors, setValues]);

  const onDocumentChange = async (event) => {
    if (event.target.files != null && event.target.files.length > 0) {
      const file = event.target.files[0];

      const result = await getThumbnailFromFile(file);
      if (result.error) toast.error(result.error);
      else onFileChange(result);
    }
  };

  const handleLocationChange = (event, value) => {
    setFieldValue('location', value);
    onFieldChange({ ...event, target: { ...event.target, name: 'location', value } });
  };

  const handleFieldChange = (event) => {
    const {
      target: { name },
    } = event;
    if (post[name] !== values[name]) onFieldChange(event);
  };

  const onCancelForm = () => {
    if (isSubmitting) return;

    onCancel();
    setErrors({});
    setFileData(getInitialFileData(post));
  };

  const onSubmitForm = (event) => {
    setAttempted(true);
    handleSubmit(event);
  };

  return (
    <form autoComplete="off" onSubmit={onSubmitForm} style={{ width: '100%' }}>
      <Grid container item xs={12} spacing={1}>
        <Grid container item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Typography variant="h4">Photo ou vidéo</Typography>
              <Typography style={{ fontWeight: 300 }}>Ajouter une photo ou une vidéo</Typography>
            </Grid>

            <Grid item xs={9}>
              <Typography variant="h4">Commentaire du post</Typography>
              <Typography style={{ fontWeight: 300 }}>Présentez-vous en quelques lignes</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid container item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <div
                className={classes.imageContainer}
                style={{
                  border: fileError ? '2px solid rgba(255, 0, 0, 0.3)' : '0',
                }}
              >
                <img className={classes.img} alt="" src={fileData.url} />
                <input
                  accept=".jpg,.jpeg.,.gif,.png,.mov,.mp4"
                  className={classes.input}
                  onChange={onDocumentChange}
                  id="postDocumentInput"
                  name="postDocument"
                  type="file"
                />
                <label className={classes.inputLabelContainer} htmlFor="postDocumentInput">
                  <IconButton
                    className={classes.inputButton}
                    color="primary"
                    aria-label="upload document"
                    component="span"
                    size="small"
                  >
                    <img alt="Upload" src={uploadLogo} />
                  </IconButton>
                  {!fileData.url && (
                    <Typography className={classes.inputLabel} variant="body2">
                      Ajouter une photo ou une vidéo
                    </Typography>
                  )}
                </label>
              </div>
              {fileError && <Typography color="error">Champs requis</Typography>}
            </Grid>

            <Grid item xs={9}>
              <TextField
                disabled={disabled}
                fullWidth
                margin="dense"
                multiline
                minRows={11}
                name="comment"
                placeholder="Cette présentation permet aux clients de mieux vous connaître"
                variant="outlined"
                value={values.comment}
                onBlur={handleFieldChange}
                onChange={handleChange}
                error={touched.comment && Boolean(errors.comment)}
                helperText={touched.comment && errors.comment}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container item xs={12} style={{ marginTop: fileError && '1rem' }}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Typography variant="h4">Hashtag</Typography>
              <Typography style={{ fontWeight: 300 }}>Ajouter vos hashtags</Typography>
            </Grid>

            <Grid item xs={9}>
              <Typography variant="h4">Localisation</Typography>
              <Typography style={{ fontWeight: 300 }}>Flaguez une ville ou un lieu ici</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid container item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <TextField
                className={classes.hashtag}
                disabled={disabled}
                fullWidth
                margin="dense"
                name="hashtag"
                placeholder="Ajoutez ici vos hashtags"
                variant="outlined"
                value={values.hashtag}
                onBlur={handleFieldChange}
                onChange={handleChange}
                error={touched.hashtag && Boolean(errors.hashtag)}
                helperText={touched.hashtag && errors.hashtag}
              />
            </Grid>

            <Grid item xs={9}>
              <Autocomplete
                disabled={disabled}
                disableClearable
                freeSolo
                inputValue={inputLocation}
                onInputChange={(event, newInputValue) => {
                  setInputLocation(newInputValue);
                }}
                value={values.location}
                onChange={handleLocationChange}
                options={locations}
                getOptionLabel={(option) =>
                  option ? `${option.properties.postcode} - ${option.properties.label}` : ''
                }
                filterOptions={(option) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="dense"
                    name="location"
                    placeholder="Recherchez une ville ou un lieu"
                    variant="outlined"
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location && errors.location}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container item alignItems="center" xs={12}>
          <DefaultCheckbox
            checked={values.isDraft}
            color="secondary"
            onChange={handleChange}
            name="isDraft"
            style={{ padding: '0', margin: '0px 8px 0px -2px' }}
          />
          <Typography>Définir comme brouillon</Typography>
        </Grid>

        <Grid className={classes.buttonContainer} container item xs={12} justifyContent="center">
          <DefaultButton cancel onClick={onCancelForm}>
            Annuler
          </DefaultButton>
          <DefaultButton
            color="secondary"
            disabled={isSubmitting || disabled || disableSubmit}
            type="submit"
            variant="contained"
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
          </DefaultButton>
        </Grid>
      </Grid>
    </form>
  );
}

export default PostForm;
